import React, { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  BadlaHistoryFilter,
  DataTable,
  HighLowPrice,
  PriceChange,
  SubHeading,
} from "../Components";
import { postAxios } from "../Services/commonService";
import { createBadla, dateFormat } from "../helper/common";

const BadlaHistory = () => {
  const [payloadData, setPayloadData] = useState({ type: true });
  const [apiUrl, setApiUrl] = useState(null);

  const { isLoading, data: historyData } = useQuery({
    queryKey: ["historyData", payloadData, apiUrl],
    queryFn: async () =>
      await postAxios({
        url: apiUrl,
        payload: payloadData,
      }),
    enabled: !!payloadData && !!apiUrl,
  });

  const columns = useMemo(() => {
    const renderCols =
      payloadData && payloadData.type
        ? [
            {
              accessorKey: "historyDate",
              header: "date/time",
              cell: ({ row }) =>
                dateFormat(row?.original?.historyDate).formattedDate,
            },
            {
              accessorKey: "maxBuyBadla",
              header: "Badla[BUY]",
              cell: ({ row }) => (
                <HighLowPrice
                  highPrice={row?.original?.maxBuyBadla}
                  lowPrice={row?.original?.minBuyBadla}
                />
              ),
            },
            {
              accessorKey: "maxSellBadla",
              header: "Badla[SELL]",
              cell: ({ row }) => (
                <HighLowPrice
                  highPrice={row?.original?.maxSellBadla}
                  lowPrice={row?.original?.minSellBadla}
                />
              ),
            },
            {
              accessorKey: "maxLTPBadla",
              header: "Badla[LTP]",
              cell: ({ row }) => (
                <HighLowPrice
                  highPrice={row?.original?.maxLTPBadla}
                  lowPrice={row?.original?.minLTPBadla}
                />
              ),
            },
          ]
        : [
            {
              accessorKey: "date",
              header: "date/time",
              cell: ({ row }) =>
                dateFormat(row?.original?.historyDate).formattedDateTime,
            },
            {
              accessorKey: "finalBadlaBuy",
              header: "Badla[BUY]",
            },
            {
              accessorKey: "finalBadlaSell",
              header: "Badla[SELL]",
            },
            {
              accessorKey: "finalBadla",
              header: "Badla[LTP]",
            },
            {
              accessorKey: "mcxBuyPrice",
              header: () => (
                <div className="text-center">
                  MCX BID <br />
                  <span className="font_small"> Comex (INR)</span>
                </div>
              ),
              cell: ({ row }) => (
                <div className="text-center">
                  <PriceChange
                    value={row?.original?.mcxBuyPrice}
                    isSubValue={true}
                    subValue={row?.original?.dollerBuy}
                  />
                </div>
              ),
            },
            {
              accessorKey: "mcxSellPrice",
              header: () => (
                <div className="text-center">
                  MCX ASK <br /> <span className="font_small">Comex (INR)</span>
                </div>
              ),
              cell: ({ row }) => (
                <div className="text-center">
                  <PriceChange
                    value={row?.original?.mcxSellPrice}
                    isSubValue={true}
                    subValue={row?.original?.dollerSell}
                  />
                </div>
              ),
            },
            {
              accessorKey: "comexBuyPrice",
              header: "COMEX BID",
              cell: ({ row }) => (
                <div className="text-center">
                  <PriceChange
                    value={row?.original?.comexBuyPrice}
                    isSubValue={true}
                    subValue={row?.original?.dollerPBuy}
                  />
                </div>
              ),
            },
            {
              accessorKey: "comexSellPrice",
              header: "COMEX ASK",
              cell: ({ row }) => (
                <div className="text-center">
                  <PriceChange value={row?.original?.comexSellPrice} />
                </div>
              ),
            },
            {
              accessorKey: "dgcxBuyPrice",
              header: "DGCX BID",
              cell: ({ row }) => (
                <div className="text-center">
                  <PriceChange
                    value={row?.original?.dgcxBuyPrice}
                    isSubValue={true}
                    subValue={row?.original?.dollerPBuy}
                    isDevide={false}
                  />
                </div>
              ),
            },
            {
              accessorKey: "dgcxSellPrice",
              header: "DGCX ASK",
              cell: ({ row }) => (
                <div className="text-center">
                  <PriceChange
                    value={row?.original?.dgcxSellPrice}
                    isSubValue={true}
                    subValue={row?.original?.dollerPSell}
                    isDevide={false}
                  />
                </div>
              ),
            },
          ];
    return renderCols;
  }, [payloadData]);

  // DataTable fetchData function
  const fetchData = useCallback(() => {
    if (!historyData) return { data: [], pages: 0 };

    return {
      data: historyData || [],
      pages: 0,
    };
  }, [historyData]);

  const handlePostRequest = ({ url, payload }) => {
    setPayloadData(payload);
    setApiUrl(url);
  };

  return (
    <>
      <SubHeading subTitle="Badla history" />

      <BadlaHistoryFilter handlePostRequest={handlePostRequest} />

      <DataTable
        columns={columns}
        fetchData={fetchData}
        isLoading={isLoading}
        isSearchable={false}
        isPagination={false}
        isPageSize={false}
        isIndexing={false}
      />
    </>
  );
};

export default BadlaHistory;
